<template>
	<div>
		<v-autocomplete
			ref="autocomplete"
			:class="dense ? 'grv-input-dense' : 'grv-input'"
			:menu-props="{
				contentClass: 'grv-menu',
				maxWidth: '991px',
				maxHeight: '50vh',
				closeOnContentClick: true
			}"
			:rules="rules"
			@update:error="errored = $event"
			:value="value"
			:loading="loading"
			:items="items"
			item-value="id"
			item-text="title"
			:search-input.sync="search"
			:readonly="readonly"
			no-filter
			clearable
			flat
			hide-details
			:name="param.name">

			<template v-slot:label>
				<Label :param="param"></Label>
			</template>

			<template v-slot:selection="{ item }">
				<span class="title mr-2">
					{{ item.title }}
					<span class="background--text text--lighten-5 body-2">
						{{ item.release_date | formatDateNoTime }}
					</span>
				</span>
			</template>

			<template v-slot:item="{ item }">
				<v-list-tile
					avatar
					:key="item.id"
					@click="emitUpdate(item)">
					<v-list-tile-avatar size="auto">
						<img
							v-if="item.poster_path"
							width="120"
							height="180"
							loading="lazy"
							:src="tmdb_img_url + item.poster_path"
							:alt="'Affiche de ' + item.title" />
						<img
							v-else
							width="120"
							height="180"
							loading="lazy"
							src="@/assets/no_img.jpg"
							alt="Pas d'affiche disponible">
					</v-list-tile-avatar>
					<v-list-tile-content>
						<v-list-tile-title class="title">
							{{ item.title }}
							<a
								:href="getTmdbImgUrl(item)"
								target="_blank"
								@click.stop
								class="tmdb-link ml-1">
								<v-icon small style="vertical-align: middle;">
									open_in_new
								</v-icon>
							</a>
						</v-list-tile-title>
						<v-list-tile-sub-title>
							<span :class="item.pgm_type === 'movie' ? 'pgm-movie-chip' : 'pgm-show-chip'">
								{{ item.pgm_type | titleize }}
							</span>
							{{ item.release_date | formatDateNoTime }}
							<span
								v-for="genre_id in item.genre_ids"
								:key="genre_id"
								class="genre-chip">
								{{ genres[genre_id] }}
							</span>
						</v-list-tile-sub-title>
						<v-list-tile-sub-title>
							{{ item.overview | substring(0, 670) }}
						</v-list-tile-sub-title>
					</v-list-tile-content>
				</v-list-tile>
			</template>

			<template v-slot:append-item>
				<v-progress-linear
					v-show="hasMorePages"
					indeterminate
					height="3"
					color="primary"
					class="ma-0"/>
			</template>

			<template v-slot:no-data>
				<v-list-tile>
					<v-list-tile-content style="align-self: center;">
						<v-list-tile-title
							v-if="loading"
							style="text-align: center;">
							<v-progress-circular
								indeterminate
								size="16"
								width="2"
								color="primary"
								class="mr-2"/>
							Recherche en cours...
						</v-list-tile-title>
						<v-list-tile-title
							v-else-if="!search || search.length < 3"
							style="text-align: center;">
							Tapez au moins 3 caractères pour lancer la recherche
						</v-list-tile-title>
						<v-list-tile-title
							v-else
							style="text-align: center;">
							Aucun résultat trouvé
						</v-list-tile-title>
					</v-list-tile-content>
				</v-list-tile>
			</template>
		</v-autocomplete>

		<Hint :param="param" :errored="errored"></Hint>
	</div>
</template>

<script>
import axios from 'axios'
import { debounce } from 'debounce'
import Label from '@/components/fields/label.vue'
import Hint from '@/components/fields/hint.vue'

const TMDB_API_URL = 'https://api.themoviedb.org/3'

export default {
	components: {
		Label,
		Hint
	},
	props: [
		'param',
		'value',
		'readonly',
		'dense',
		'form'
	],
	data () {
		return {
			tmdb_img_url: 'https://image.tmdb.org/t/p/w188_and_h282_bestv2',
			errored: false,
			items: [],
			rules: [],
			search: null,
			loading: false,
			genres: {},
			current_page: 1,
			total_pages: 1,
			current_search: '',
			menu_content: null,
			scroll_handler: null
		}
	},
	computed: {
		hasMorePages () {
			return this.current_page < this.total_pages && !this.loading
		}
	},
	watch: {
		search (val) {
			if (!val) return
			this.current_page = 1
			this.total_pages = 1
			this.items = []
			if (val.length < 3) return
			this.searchTmdb(val)
		},
		items () {
			this.$nextTick(() => {
				this.setupScrollListener()
			})
		}
	},
	methods: {
		getTmdbOptions (extraParams = {}) {
			return {
				method: 'get',
				headers: { accept: 'application/json' },
				params: {
					api_key: window.appConfig._$6pu8jx,
					language: 'fr-FR',
					...extraParams
				}
			}
		},
		getTmdbImgUrl (item) {
			const type = item.pgm_type === 'movie' ? 'movie' : 'tv'
			return `https://www.themoviedb.org/${type}/${item.id}`
		},
		emitUpdate (item) {
			let int = parseInt(item.id)
			if (Number.isNaN(int)) int = ''
			this.$emit('update:value', int)
			let hasPgmType = this.$commons.deepGet(
				this.form,
				'parameters.program_type'
			) !== undefined
			if (hasPgmType) {
				let programType = this.$commons.deepGet(
					this.form,
					'parameters.program_type',
					null
				)
				if (programType !== item.pgm_type) {
					this.$emit('update-other-parameter', {
						name: 'program_type',
						value: item.pgm_type
					})
					this.$Toaster({
						title: 'TMDB',
						content: `Le type de programme a été mis à jour : ${item.pgm_type}`,
						type: 'info',
						timeout: 3000
					})
				}
			}
		},
		tmdbIdRule (val) {
			if (!val) return true
			return !isNaN(Number(val)) || 'Id numerique uniquement'
		},
		requiredCheck (value) {
			if (value === '') return 'Requis'
			if (value === null) return 'Requis'
			if (value === undefined) return 'Requis'
			return true
		},
		searchTmdb: debounce(function (search, page = 1) {
			this.fetchTmdbResults(search, page)
		}, 300),
		fetchTmdbResults (search, page = 1) {
			if (!search) return
			this.loading = true
			this.current_search = search

			let programType = this.$commons.deepGet(
				this.form,
				'parameters.program_type',
				null
			)
			let url = `${TMDB_API_URL}/search/multi`
			if (programType === 'movie') {
				url = `${TMDB_API_URL}/search/movie`
			} else if (programType === 'show') {
				url = `${TMDB_API_URL}/search/tv`
			}

			axios(url, this.getTmdbOptions({ page, query: search, include_adult: false }))
				.then(response => {
					const results = response.data.results.map(result => {
						result.pgm_type = result.title ? 'movie' : 'show'
						result.title = result.title || result.name
						result.original_title = result.original_title || result.original_name
						result.release_date = result.release_date || result.first_air_date
						delete result.name
						delete result.original_name
						delete result.first_air_date
						delete result.video
						delete result.origin_country
						return result
					})
					if (page === 1) {
						this.items = results
					} else {
						// Utiliser $set pour garantir la réactivité
						this.$set(this, 'items', [...this.items, ...results])
					}
					this.current_page = response.data.page
					this.total_pages = response.data.total_pages

					// Forcer le VSelect à afficher tous les items
					this.$nextTick(() => {
						if (this.$refs.autocomplete) {
							this.$refs.autocomplete.lastItem = this.items.length + response.data.results.length
						}
					})
				})
				.catch(error => {
					this.$Toaster({
						title: 'TMDB search Error',
						content: error.message,
						type: 'error'
					})
				})
				.finally(() => {
					this.loading = false
				})
		},
		findValueResult () {
			if (!this.value) return null
			this.loading = true

			const options = this.getTmdbOptions()

			// Essayer d'abord en tant que film
			axios(`${TMDB_API_URL}/movie/${this.value}`, options)
				.then(response => {
					this.handleFindResult(response.data, 'movie')
				})
				.catch(error => {
					// Si 404, essayer en tant que série TV
					if (error.response && error.response.status === 404) {
						return axios(`${TMDB_API_URL}/tv/${this.value}`, options)
							.then(response => {
								this.handleFindResult(response.data, 'show')
							})
					}
					throw error
				})
				.catch(error => {
					this.$Toaster({
						title: 'TMDB find Error',
						content: error.message,
						type: 'error'
					})
				})
				.finally(() => {
					this.loading = false
				})
		},
		handleFindResult (data, pgm_type) {
			const item = {
				id: data.id,
				pgm_type: pgm_type,
				title: data.title || data.name,
				original_title: data.original_title || data.original_name,
				release_date: data.release_date || data.first_air_date,
				poster_path: data.poster_path,
				overview: data.overview,
				genre_ids: data.genres ? data.genres.map(g => g.id) : []
			}
			this.items = [item]
		},
		setupScrollListener () {
			// Trouver le conteneur du menu (v-menu__content)
			if (!this.$refs.autocomplete) return
			if (!this.$refs.autocomplete.$refs.menu) return
			const menuContent = this.$refs.autocomplete.$refs.menu.$refs.content
			if (!menuContent) return

			// Éviter d'ajouter plusieurs listeners
			if (this.menu_content === menuContent) return

			// Cleanup ancien listener si existe
			this.removeScrollListener()

			this.menu_content = menuContent
			this.scroll_handler = this.onMenuScroll.bind(this)
			this.menu_content.addEventListener('scroll', this.scroll_handler)
		},
		removeScrollListener () {
			if (this.menu_content && this.scroll_handler) {
				this.menu_content.removeEventListener('scroll', this.scroll_handler)
				this.menu_content = null
				this.scroll_handler = null
			}
		},
		onMenuScroll (event) {
			const container = event.target
			const threshold = 50 // pixels avant le bas
			const isNearBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - threshold

			if (isNearBottom && this.hasMorePages) {
				this.loadNextPage()
			}
		},
		loadNextPage () {
			if (this.loading || !this.hasMorePages) return
			this.fetchTmdbResults(this.current_search, this.current_page + 1)
		},
		getGenres () {
			const CACHE_KEY = 'tmdb_genres'
			const CACHE_DURATION = 24 * 60 * 60 * 1000 // 24h en millisecondes

			// Vérifier le cache
			const cached = window.localStorage.getItem(CACHE_KEY)
			if (cached) {
				try {
					const { data, timestamp } = JSON.parse(cached)
					if (Date.now() - timestamp < CACHE_DURATION) {
						this.genres = data
						return
					}
				} catch (e) {
					window.localStorage.removeItem(CACHE_KEY)
				}
			}

			// Fetch depuis l'API
			const options = this.getTmdbOptions({ include_adult: false })
			let genres = []
			axios(`${TMDB_API_URL}/genre/movie/list`, options)
				.then(response => {
					genres = response.data.genres
					return axios(`${TMDB_API_URL}/genre/tv/list`, options)
				})
				.then(response => {
					genres = genres.concat(response.data.genres)
					this.genres = genres.reduce((acc, genre) => {
						acc[genre.id] = genre.name
						return acc
					}, {})

					// Sauvegarder en cache
					window.localStorage.setItem(CACHE_KEY, JSON.stringify({
						data: this.genres,
						timestamp: Date.now()
					}))
				})
				.catch(error => {
					this.$Toaster({
						title: 'TMDB fetch genres Error',
						content: error.message,
						type: 'error'
					})
				})
		}
	},
	mounted () {
		if (!this.param) return
		this.getGenres()
		if (this.param.required || this.param.required === undefined) {
			this.rules.push(this.requiredCheck)
		}
		this.rules.push(this.tmdbIdRule)
		if (this.value) {
			this.findValueResult()
		}
	},
	beforeDestroy () {
		this.removeScrollListener()
	}
}
</script>

<style lang="stylus" scoped>
>>>.v-list__tile
	align-items: start
	height: inherit
	min-height: 56px
	padding: 0 4px 0 0
	.v-list__tile__avatar
		min-width: 120px
		align-self: start
		.v-avatar
			border-radius: 0
			img
				border-radius: 0
				width: 120px
				height: 180px
	.v-list__tile__content
		padding-left: 12px
		.v-list__tile__title
			height: 38px
			align-content: center
			.tmdb-link
				color: var(--v-primary-base)
				text-decoration: none
				opacity: 0.7
				&:hover
					opacity: 1
		.v-list__tile__sub-title
			white-space: normal
			padding-bottom: 8px
			.genre-chip
				border-radius: 4px
				margin-left: 8px
				padding: 0 4px
				background-color: var(--v-background-base)
				border: 1px solid var(--v-background-lighten2)
			.pgm-movie-chip
			.pgm-show-chip
				border-radius: 4px
				margin-right: 8px
				padding: 0 4px
			.pgm-movie-chip
				color: black
				background-color: var(--v-green-base)
				border: 1px solid var(--v-green-lighten2)
			.pgm-show-chip
				color: black
				background-color: var(--v-blue-base)
				border: 1px solid var(--v-blue-lighten2)
</style>



// WEBPACK FOOTER //
// src/components/fields/tmdb_id.vue